import React, { useState, useEffect } from 'react';

import { useToast } from '@chakra-ui/react';

import { deleteSites, exportCustomerSites } from 'api/site';
import useQueryParams from 'hooks/useQueryParams';
import useUrlSharing from 'hooks/useUrlSharing';

import { useMutation, useQueryClient } from 'react-query';

import { washHub } from 'store/slices';

import { CustomerSitesProps } from './CustomerSites.props';

import CustomerSitesView from './CustomerSites.view';

const CustomerSitesContainer = ({}: CustomerSitesProps) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [defaultIndex, setDefaultIndex] = useState<number>(0);
  const [urlRecord, setUrlRecord] = useState({});
  const [search, setSearch] = useState<string>('');
  const [sortBy, setSortBy] = useState<string | undefined>('name');
  const [sort, setSort] = useState<number | undefined>(1);
  const selectedWashHub = washHub.selectors.useSelectedWashHub();
  const washHubId = selectedWashHub?._id || '';

  const queryUrl = useQueryParams();
  useUrlSharing(urlRecord, washHubId);

  const { mutate, isLoading: isDeleting } = useMutation(deleteSites);

  const { mutate: exportMutate, isLoading: isExporting } = useMutation(
    exportCustomerSites,
    {
      onSuccess: () => {
        toast({
          duration: 3000,
          title: 'Success',
          isClosable: true,
          status: 'success',
          description: 'Customer Sites exported successfully',
        });
      },
      onError: () => {
        toast({
          duration: 3000,
          title: 'Error',
          isClosable: true,
          status: 'error',
          description: 'Failed to export Customer Sites',
        });
      },
    },
  );

  // Map tab index to status filter for export
  // API expects lowercase status values: 'active', 'inactive', 'archived'
  const getStatusForExport = (): string[] | undefined => {
    switch (defaultIndex) {
      case 1: // Active tab
        return ['active'];
      case 2: // Inactive tab
        return ['inactive'];
      case 3: // Archived tab
        return ['archived'];
      default:
        // All tab
        return undefined;
    }
  };

  const onExport = () => {
    const status = getStatusForExport();
    exportMutate({
      washHub: washHubId || undefined,
      washHubName: selectedWashHub?.name,
      status,
      search: search || undefined,
    });
  };

  useEffect(() => {
    const urlParams = {
      washHubId: String(washHubId),
      ...(defaultIndex ? { defaultIndex: `${defaultIndex}` } : {}),
    };
    setUrlRecord(urlParams);
  }, [washHubId, defaultIndex]);

  useEffect(() => {
    const { defaultIndex } = queryUrl;
    defaultIndex && setDefaultIndex(+defaultIndex || 0);
  }, []);

  const onDelete = (ids: string[], _onSuccess: () => void) =>
    mutate(ids, {
      onSuccess: () => {
        toast({
          duration: 3000,
          title: 'Success',
          isClosable: true,
          status: 'success',
          description: 'Customer Sites Deleted',
        });

        queryClient.invalidateQueries(`fetch-sites-${washHubId}`);
        queryClient.invalidateQueries(`fetch-sites-all-${washHubId}`);
        queryClient.invalidateQueries(`fetch-sites-active-${washHubId}`);
        queryClient.invalidateQueries(`fetch-sites-inactive-${washHubId}`);
        queryClient.invalidateQueries(`fetch-sites-archived-${washHubId}`);

        queryClient.invalidateQueries(`fetch-sites-${washHubId}-select`, {
          exact: true,
        });
        queryClient.invalidateQueries(
          `fetch-sites-active-${washHubId}-select`,
          {
            exact: true,
          },
        );

        queryClient.invalidateQueries('fetch-sites-select-all', {
          exact: true,
        });
        queryClient.invalidateQueries('fetch-sites-active-select-all', {
          exact: true,
        });

        queryClient.invalidateQueries(['fetch-sites-active']);

        _onSuccess();
      },
    });

  const props = {
    isDeleting,
    defaultIndex,
    setDefaultIndex,
    onDelete,
    search,
    setSearch,
    sortBy,
    setSortBy,
    sort,
    setSort,
    onExport,
    isExporting,
  };

  return <CustomerSitesView {...props} />;
};

export default CustomerSitesContainer;
